import { Component, Input } from '@builder.io/sdk';

import sectionFields from 'molecules/Section/config';

const tableCell = [
  {
    name: 'cell',
    type: 'string',
    enum: [
      { label: 'Check', value: 'check' },
      { label: 'X-Mark', value: 'x-mark' },
      { label: 'Partial/Warning', value: 'warning' },
      { label: 'Text', value: 'text' },
    ],
    defaultValue: 'check',
    helperText: 'Type of content for this cell: checkmark, x-mark, warning, or custom text.',
  },
  {
    name: 'text',
    type: 'string',
    showIf: "options.get('cell') === 'text'",
    helperText: 'Custom text displayed when "Text" is selected as the cell type.',
  },
  {
    name: 'label',
    type: 'string',
    helperText: 'Optional label to display next to the icon (e.g., "Purpose-built", "Retrofitted").',
  },
] as const satisfies DeepReadonly<Input[]>;

const tableRow = [
  {
    name: 'rowHeading',
    type: 'string',
    required: true,
    helperText: 'Label for the feature or detail being compared in this row.',
  },
  {
    name: 'columnOne',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for Column One.',
  },
  {
    name: 'columnTwo',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for Column Two.',
  },
  {
    name: 'columnThree',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for Column Three.',
  },
] as const satisfies DeepReadonly<Input[]>;

const flexibleComparisonColumnFields = [
  {
    name: 'id',
    type: 'string',
    showIf: 'false',
    helperText: 'Auto-generated ID. Hidden from the editor.',
  },
  {
    name: 'title',
    type: 'string',
    defaultValue: 'Column',
    helperText: 'Column header text. Not shown when the Prismatic logo is on.',
  },
  {
    name: 'showPrismaticLogo',
    type: 'boolean',
    defaultValue: false,
    helperText: 'Show the Prismatic logo instead of the title text.',
  },
  {
    name: 'highlighted',
    type: 'boolean',
    defaultValue: false,
    helperText: 'Highlight this column with the Prismatic gradient.',
  },
] as const satisfies DeepReadonly<Input[]>;

const flexibleComparisonCellFields = [
  {
    name: 'icon',
    type: 'string',
    enum: [
      { label: 'None', value: 'none' },
      { label: 'Check', value: 'check' },
      { label: 'X-Mark', value: 'x-mark' },
      { label: 'Partial/Warning', value: 'warning' },
    ],
    defaultValue: 'none',
    helperText: 'Optional icon shown before the text.',
  },
  {
    name: 'text',
    type: 'string',
    defaultValue: '',
    helperText: 'Cell text. Can be combined with an icon or left empty to show only the icon.',
  },
] as const satisfies DeepReadonly<Input[]>;

const flexibleComparisonRowFields = [
  {
    name: 'id',
    type: 'string',
    showIf: 'false',
    helperText: 'Auto-generated ID. Hidden from the editor.',
  },
  {
    name: 'rowHeading',
    type: 'string',
    defaultValue: '',
    helperText: 'Label for the feature or detail being compared in this row.',
  },
  {
    name: 'cells',
    type: 'list',
    subFields: flexibleComparisonCellFields,
    defaultValue: [
      { icon: 'none', text: '' },
      { icon: 'none', text: '' },
    ],
    helperText:
      'One cell per column, in the same order as the columns. Extra cells are ignored and missing cells show empty.',
  },
] as const satisfies DeepReadonly<Input[]>;

// Content saved before the editable Columns/Rows layout existed lives in tableSections.
// While any of it has rows, the classic three-column table renders and its fields stay visible;
// otherwise the editable layout is used and the classic fields are hidden.
// Builder only prepends `return` to showIf strings without ";" or " return ", so this must stay a
// single expression. Sections and rows can arrive as Immutable.js collections or plain arrays.
const HAS_CLASSIC_ROWS =
  "(sections => !!sections && (sections.toJS ? sections.toJS() : sections).some(section => (rows => !!rows && (rows.toJS ? rows.toJS() : rows).length > 0)(section && (section.get ? section.get('rows') : section.rows))))(options.get('tableSections'))";

const CLASSIC_SHOW_IF = HAS_CLASSIC_ROWS;
const FLEXIBLE_SHOW_IF = `!${HAS_CLASSIC_ROWS}`;

const featureComparisonTableFields = [
  {
    name: 'tableHeading',
    type: 'string',
    defaultValue: 'Features',
    helperText: 'Main heading displayed above the comparison table.',
  },
  {
    name: 'columns',
    type: 'list',
    subFields: flexibleComparisonColumnFields,
    defaultValue: [
      { id: 'col-0', title: 'Prismatic', showPrismaticLogo: true, highlighted: true },
      { id: 'col-1', title: 'Column 2', showPrismaticLogo: false, highlighted: false },
    ],
    showIf: FLEXIBLE_SHOW_IF,
    helperText:
      'Table columns, in display order. Add, remove or rename as needed. Remember to update the cells in each row to match.',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('columns')?.length > 0) {
        options.get('columns').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `col-${index}`);
        });
      }
    },
  },
  {
    name: 'rows',
    type: 'list',
    subFields: flexibleComparisonRowFields,
    defaultValue: [
      {
        id: 'row-0',
        rowHeading: 'Row 1',
        cells: [
          { icon: 'check', text: '' },
          { icon: 'x-mark', text: '' },
        ],
      },
      {
        id: 'row-1',
        rowHeading: 'Row 2',
        cells: [
          { icon: 'none', text: 'Text' },
          { icon: 'none', text: 'Text' },
        ],
      },
    ],
    showIf: FLEXIBLE_SHOW_IF,
    helperText: 'Table rows. Add, remove or reorder as needed.',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('rows')?.length > 0) {
        options.get('rows').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `row-${index}`);
        });
      }
    },
  },
  // Column One
  {
    name: 'columnOneTitle',
    showIf: CLASSIC_SHOW_IF,
    type: 'string',
    required: true,
    defaultValue: 'In-House',
    helperText: 'Title for Column One (e.g., In-House).',
  },
  {
    name: 'columnOneHighlighted',
    showIf: CLASSIC_SHOW_IF,
    type: 'boolean',
    defaultValue: false,
    helperText: 'Highlight Column One with gradient effect.',
  },
  {
    name: 'columnOneIsPrismatic',
    showIf: CLASSIC_SHOW_IF,
    type: 'boolean',
    defaultValue: false,
    helperText: 'Show Prismatic logo instead of title text for Column One.',
  },
  // Column Two
  {
    name: 'columnTwoTitle',
    showIf: CLASSIC_SHOW_IF,
    type: 'string',
    required: true,
    defaultValue: 'Traditional iPaaS',
    helperText: 'Title for Column Two (e.g., Traditional iPaaS).',
  },
  {
    name: 'columnTwoHighlighted',
    showIf: CLASSIC_SHOW_IF,
    type: 'boolean',
    defaultValue: false,
    helperText: 'Highlight Column Two with gradient effect.',
  },
  {
    name: 'columnTwoIsPrismatic',
    showIf: CLASSIC_SHOW_IF,
    type: 'boolean',
    defaultValue: false,
    helperText: 'Show Prismatic logo instead of title text for Column Two.',
  },
  // Column Three
  {
    name: 'columnThreeTitle',
    showIf: CLASSIC_SHOW_IF,
    type: 'string',
    required: true,
    defaultValue: 'Prismatic',
    helperText: 'Title for Column Three.',
  },
  {
    name: 'columnThreeHighlighted',
    showIf: CLASSIC_SHOW_IF,
    type: 'boolean',
    defaultValue: true,
    helperText: 'Highlight Column Three with gradient effect.',
  },
  {
    name: 'columnThreeIsPrismatic',
    showIf: CLASSIC_SHOW_IF,
    type: 'boolean',
    defaultValue: true,
    helperText: 'Show Prismatic logo instead of title text for Column Three.',
  },
  {
    name: 'tableSections',
    showIf: CLASSIC_SHOW_IF,
    type: 'list',
    defaultValue: [
      {
        id: 'tableSections-0',
        label: 'Features',
        rows: [],
      },
    ],
    subFields: [
      {
        name: 'id',
        type: 'string',
        helperText: 'Auto-generated unique ID for each section.',
      },
      {
        name: 'label',
        type: 'string',
        defaultValue: 'Features',
        helperText: 'Section label (e.g., Features, Capabilities, Performance).',
      },
      {
        name: 'rows',
        type: 'list',
        subFields: tableRow,
        helperText: 'Rows of features compared across the three columns.',
      },
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('tableSections').length > 0) {
        options.get('tableSections').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `tableSections-${index}`);
        });
      }
    },
    helperText:
      'Classic three-column content. To switch this table to the editable Columns and Rows layout, remove every row here.',
  },
  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const featureComparisonTableConfig = {
  name: 'Feature Comparison Table',
  inputs: featureComparisonTableFields,
} as Component;

export type FlexibleComparisonColumnProps = GenerateBuilder<typeof flexibleComparisonColumnFields>;
export type FlexibleComparisonCellProps = GenerateBuilder<typeof flexibleComparisonCellFields>;
export type FlexibleComparisonRowProps = GenerateBuilder<typeof flexibleComparisonRowFields>;
export type FeatureComparisonCellProps = GenerateBuilder<typeof tableCell>;
export type FeatureComparisonRowProps = GenerateBuilder<typeof tableRow>;
export type FeatureComparisonTableConfigProps = GenerateBuilder<typeof featureComparisonTableFields>;

export default featureComparisonTableConfig;
